import React, { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GraduationCap, TriangleAlert } from "lucide-react";
import dayjs from "dayjs";
import TimePickerCst from "@components/InputCst/TimePickerCst";
import SuccessDialogCst from "@components/SuccessDialogCst";
import WarningDialogCst from "@components/WarningDialogCst";
import { EditionSession } from "../types";
import {
  getRescheduleMinStart,
  getRescheduleStartError,
  getSessionTimeStatus,
  hasSessionOverlap,
} from "../sessionChecks";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

interface RescheduleSessionModalProps {
  open: boolean;
  session: EditionSession;
  sessionNumber: number;
  contextLabel?: string;
  publishDate?: string | number | null;
  otherSessions: EditionSession[];
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (startDateIso: string, endDateIso: string) => void | Promise<void>;
}

const RescheduleSessionModal: React.FC<RescheduleSessionModalProps> = ({
  open,
  session,
  sessionNumber,
  contextLabel,
  publishDate,
  otherSessions,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["session-detail", "session-wizard"]);

  const [start, setStart] = useState<string | null>(
    session.start_date ? dayjs(session.start_date).toISOString() : null,
  );
  const [end, setEnd] = useState<string | null>(
    session.end_date ? dayjs(session.end_date).toISOString() : null,
  );
  const [attempted, setAttempted] = useState(false);
  const [now, setNow] = useState(() => dayjs());
  const [overlapOpen, setOverlapOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const timeStatus = getSessionTimeStatus(session, now);
  const locked = timeStatus !== "future";

  const errors = useMemo(() => {
    const e: { start?: string; end?: string } = {};
    if (!start) e.start = String(t("session-wizard:errors.required"));
    if (!end) e.end = String(t("session-wizard:errors.required"));
    if (start && end && !dayjs(end).isAfter(dayjs(start))) {
      e.end = String(t("session-wizard:errors.end-before-start"));
    }
    const startError = getRescheduleStartError(
      start,
      session.start_date,
      publishDate,
      now,
    );
    if (startError) {
      e.start = String(
        t(`session-wizard:errors.reschedule-${startError}`, {
          date: publishDate ? dayjs(publishDate).format("DD/MM/YYYY") : "",
          interpolation: { escapeValue: false },
        }),
      );
    }
    return e;
  }, [start, end, session.start_date, publishDate, now, t]);

  const valid = !errors.start && !errors.end;

  const handleConfirm = async () => {
    const current = dayjs();
    setAttempted(true);
    setNow(current);
    if (
      getSessionTimeStatus(session, current) !== "future" ||
      getRescheduleStartError(start, session.start_date, publishDate, current)
    ) {
      return;
    }
    if (!valid || !start || !end) return;
    if (
      hasSessionOverlap({ start_date: start, end_date: end }, otherSessions)
    ) {
      setOverlapOpen(true);
      return;
    }
    try {
      await onConfirm(start, end);
    } catch (e: any) {
      setSubmitError(
        e?.message ||
          String(t("session-detail:reschedule-modal.error-generic")),
      );
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "20px 24px 8px",
            color: theme.palette.text.primary,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            {contextLabel && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: theme.palette.text.primary,
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                <GraduationCap size={14} />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {contextLabel}
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
              {t("session-detail:reschedule-modal.title", {
                number: sessionNumber,
              })}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "8px 24px 24px",
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          }}
        >
          <Grid container spacing={2} sx={{ mt: "10px" }}>
            <Grid item xs={12} md={6}>
              <TimePickerCst
                id="reschedule-session-start"
                label={String(t("session-wizard:main.start-date"))}
                value={start ? dayjs(start) : null}
                onChange={(v: any) => {
                  if (v && !dayjs(v).isValid()) return;
                  setStart(v ? dayjs(v).toISOString() : null);
                }}
                required
                disabled={locked}
                minDate={getRescheduleMinStart(publishDate, now).toISOString()}
                error={attempted && !!errors.start}
                errorMessage={attempted ? errors.start : undefined}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TimePickerCst
                id="reschedule-session-end"
                label={String(t("session-wizard:main.end-date"))}
                value={end ? dayjs(end) : null}
                onChange={(v: any) => {
                  if (v && !dayjs(v).isValid()) return;
                  setEnd(v ? dayjs(v).toISOString() : null);
                }}
                required
                disabled={locked}
                minDate={start || undefined}
                error={attempted && !!errors.end}
                errorMessage={attempted ? errors.end : undefined}
              />
            </Grid>
          </Grid>
          {locked && (
            <Typography
              sx={{
                fontSize: "11px",
                color: theme.palette.warning.main,
                mt: 1,
              }}
            >
              {t(`session-wizard:reschedule-locked.${timeStatus}`)}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "12px 24px 20px" }}>
          <ButtonSecondary size="md" onClick={onClose} disabled={isSubmitting}>
            {t("session-detail:reschedule-modal.cancel")}
          </ButtonSecondary>
          <Box sx={{ flex: 1 }} />
          <ButtonPrimary
            size="md"
            onClick={handleConfirm}
            disabled={isSubmitting || locked || (attempted && !valid)}
          >
            {isSubmitting
              ? t("session-detail:reschedule-modal.submitting")
              : t("session-detail:reschedule-modal.confirm")}
          </ButtonPrimary>
        </DialogActions>
      </Dialog>
      <SuccessDialogCst
        open={overlapOpen}
        icon={<TriangleAlert size={40} />}
        title={String(t("session-wizard:overlap.title"))}
        description={String(t("session-wizard:overlap.description"))}
        actionLabel={String(t("session-wizard:overlap.confirm"))}
        onClose={() => setOverlapOpen(false)}
      />
      <WarningDialogCst
        open={!!submitError}
        title={String(t("session-detail:reschedule-modal.error-title"))}
        description={submitError ?? ""}
        closeLabel={String(t("session-detail:reschedule-modal.error-close"))}
        onClose={() => setSubmitError(null)}
      />
    </>
  );
};

export default RescheduleSessionModal;
